import * as React from "react";

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

export type TableVariantT = "default" | "thalo";

export const tableVariants = cva("w-full caption-bottom text-sm", {
  variants: {
    variant: {
      default: "",
      thalo: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & VariantProps<typeof tableVariants>
>(({ className, variant, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn(tableVariants({ variant }), className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const tableHeaderVariants = cva("[&_tr]:border-b", {
  variants: {
    variant: {
      default: "",
      thalo: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableHeaderVariants>
>(({ className, variant, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(tableHeaderVariants({ variant }), className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const tableBodyVariants = cva("[&_tr:last-child]:border-0", {
  variants: {
    variant: {
      default: "",
      thalo: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableBodyVariants>
>(({ className, variant, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(tableBodyVariants({ variant }), className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const tableFooterVariants = cva(
  "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
  {
    variants: {
      variant: {
        default: "",
        thalo: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof tableFooterVariants>
>(({ className, variant, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(tableFooterVariants({ variant }), className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const tableRowVariants = cva(
  "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
  {
    variants: {
      variant: {
        default: "",
        thalo: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> &
  VariantProps<typeof tableRowVariants>
>(({ className, variant, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(tableRowVariants({ variant }), className)}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const tableCellVariants = cva(
  "p-4 align-middle [&:has([role=checkbox])]:pr-0",
  {
    variants: {
      variant: {
        default: "",
        thalo: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> &
  VariantProps<typeof tableCellVariants>
>(({ className, variant, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(tableCellVariants({ variant }), className)}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
